import React from "react";
import { colors } from '../../../../theme/colors';
import {
  Button,
  Stack,
  Typography,
  CircularProgress,
  MenuItem,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Zoom,
  Skeleton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { CustomTextField } from "../../../../components/common-components/CustomTextField";
import {
  CustomSelect,
  CustomSelectField,
} from "../../../../components/common-components/CustomSelect";
import { MainWrapper } from "./style";
import useCreateQuestion from "./useCreateQuestion";
import CustomConfirmModel from "../../../../components/common-components/CustomConfirmModel";

const tooltipProps = {
  placement: "top",
  arrow: true,
  slots: { transition: Zoom },
  PopperProps: {
    modifiers: [
      {
        name: "preventOverflow",
        options: { boundary: "window" },
      },
    ],
  },
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: colors.primary,
        color: "#fff",
        fontSize: "0.875rem",
      },
    },
    arrow: {
      sx: { color: colors.primary },
    },
  },
};

const SkeletonRow = () => (
  <TableRow>
    <TableCell>
      <Skeleton variant="text" width={40} height={26} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width="85%" height={26} />
    </TableCell>
    <TableCell align="center">
      <Box className="action-buttons-container">
        <Skeleton variant="circular" width={30} height={30} />
        <Skeleton variant="circular" width={30} height={30} />
      </Box>
    </TableCell>
  </TableRow>
);

const CreateCustomQuestion = () => {
  const {
    selectedRole,
    jdLevel,
    setJdLevel,
    showQuestionInput,
    questionText,
    questionsList,
    rolesOptions,
    isLoadingRoles,
    isLoadingQuestions,
    isSubmitting,
    error,
    isEditing,
    handleRoleChange,
    handleCreateQuestionClick,
    handleQuestionChange,
    handleSaveQuestion,
    handleCancelQuestion,
    handleEditQuestion,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    deleteConfirmation,
    levelOptions,
    page,
    rowsPerPage,
    isFirstPage,
    isLastPage,
    handlePrevPage,
    handleNextPage,
    handleRowsPerPageChange,
  } = useCreateQuestion();

  const getTooltipTitle = (action) => {
    return !jdLevel ? "Please select the experience level" : action;
  };

  return (
    <MainWrapper>
      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
        Create Custom Question
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a role and experience level to manage custom interview questions.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "12px",
          border: "1px solid rgba(145, 158, 171, 0.24)",
          backgroundColor: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2, flex: 1, minWidth: "280px" }}>
            <CustomSelect
              className="role-dropdown required_field"
              options={rolesOptions}
              getOptionLabel={(option) => option?.role || ""}
              value={selectedRole}
              onChange={handleRoleChange}
              disabled={isLoadingRoles}
              renderInput={(params) => (
                <CustomSelectField
                  {...params}
                  label="Role"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingRoles && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  className="role-menu-item"
                  sx={{
                    "&:hover": { color: colors.primary },
                    "&.Mui-focused": { color: colors.primary },
                  }}
                >
                  {option?.role || ""}
                </MenuItem>
              )}
            />

            <CustomSelect
              options={levelOptions}
              value={
                jdLevel
                  ? levelOptions.find((opt) => opt.value === jdLevel) || null
                  : null
              }
              getOptionLabel={(option) => option?.label || option || ""}
              onChange={(event, value) => setJdLevel(value?.value || value || "")}
              disabled={!selectedRole}
              renderInput={(params) => (
                <CustomSelectField
                  {...params}
                  label="Experience Level"
                  variant="outlined"
                  sx={{ minWidth: "260px" }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>{option.label || ""}</MenuItem>
              )}
            />
          </Stack>

          <Button
            className="btn"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateQuestionClick}
            disabled={!selectedRole || !jdLevel || isLoadingRoles || isEditing}
            sx={{ height: "42px", px: 3, fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Create Custom Question
          </Button>
        </Box>
      </Paper>

      {showQuestionInput && (
        <Box className="question-input-wrapper">
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
              {isEditing ? "Edit Question" : "New Custom Question"}
            </Typography>
            <CustomTextField
              className="required_field"
              label="Question"
              placeholder="Enter your custom interview question..."
              variant="outlined"
              value={questionText}
              onChange={handleQuestionChange}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              error={!!error}
              helperText={error}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (questionText.trim() && !isSubmitting) {
                    handleSaveQuestion();
                  }
                }
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              💡 Press <strong>Enter</strong> to save, <strong>Shift + Enter</strong> for new line
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Button
                className="cancel-btn"
                variant="outlined"
                onClick={handleCancelQuestion}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="btn"
                variant="contained"
                onClick={handleSaveQuestion}
                disabled={!questionText.trim() || isSubmitting}
                sx={{ px: 3 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isEditing ? (
                  "Update Question"
                ) : (
                  "Save Question"
                )}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      <Box sx={{ marginTop: "28px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant="h6" fontWeight="600" color="primary">
            Saved Questions
          </Typography>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel className="rows-per-page-label">
              Rows per Page
            </InputLabel>
            <Select
              className="rows-per-page-select"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              label="Rows per Page"
              size="small"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer className="custom-scrollbar table-container">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "80px" }}>S.No</TableCell>
                <TableCell>Question</TableCell>
                <TableCell align="center" sx={{ width: "150px" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoadingQuestions ? (
                Array.from({ length: rowsPerPage || 5 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : questionsList.length > 0 ? (
                questionsList.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell sx={{ width: "80px" }}>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ maxWidth: "600px" }}>
                      <Tooltip title={item.question} placement="top" arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.5,
                            color: "#334155",
                          }}
                        >
                          {item.question}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: "150px" }}>
                      <Box className="action-buttons-container">
                        <Tooltip
                          title={getTooltipTitle("Edit")}
                          {...tooltipProps}
                        >
                          <span>
                            <IconButton
                              variant="outlined"
                              onClick={() => handleEditQuestion(item.id)}
                              size="small"
                              className="edit-button"
                              disabled={!jdLevel || isSubmitting}
                            >
                              <EditIcon className="icon-large" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={getTooltipTitle("Delete")}
                          {...tooltipProps}
                        >
                          <span>
                            <IconButton
                              variant="outlined"
                              onClick={() => handleDeleteClick(item.id)}
                              size="small"
                              className="delete-button"
                              disabled={!jdLevel || isSubmitting}
                            >
                              <DeleteIcon className="icon-large" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <HelpOutlineIcon sx={{ fontSize: 52, color: "#cbd5e1" }} />
                      <Typography variant="h6" fontWeight={600} color="text.secondary">
                        {!selectedRole || !jdLevel
                          ? "No Role & Level Selected"
                          : "No questions found"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: "center" }}>
                        {!selectedRole || !jdLevel
                          ? "Please select a Role and Experience Level above to view or create custom interview questions."
                          : "No custom questions found for this role and experience level. Click 'Create Custom Question' to add one."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className="pagination-container">
          {isLoadingQuestions ? (
            <>
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
              <Skeleton variant="text" width={60} height={28} />
              <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "8px" }} />
            </>
          ) : (
            <>
              <Button
                className="btn"
                variant="contained"
                onClick={handlePrevPage}
                disabled={isFirstPage}
                startIcon={<NavigateBeforeIcon />}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Page {page}
              </Typography>
              <Button
                className="btn"
                variant="contained"
                onClick={handleNextPage}
                disabled={isLastPage}
                endIcon={<NavigateNextIcon />}
              >
                Next
              </Button>
            </>
          )}
        </Box>
      </Box>

      <CustomConfirmModel
        title="Delete Question"
        textContent={
          deleteConfirmation.question
            ? `Are you sure you want to delete "${
                deleteConfirmation.question.question.length > 60
                  ? deleteConfirmation.question.question.substring(0, 60) + "..."
                  : deleteConfirmation.question.question
              }"?`
            : "Are you sure you want to delete this question?"
        }
        confirmButton="Yes"
        open={deleteConfirmation.open}
        handleClose={handleDeleteCancel}
        handleAction={handleDeleteConfirm}
        name=""
      />
    </MainWrapper>
  );
};

export default CreateCustomQuestion;

